/**
 * Versiones de un modelo (`/api/versiones`).
 *
 * La versión porta los INDICADORES; la estructura es del modelo y se comparte.
 * Versionar copia indicadores, no estructura.
 *
 * La versión es AGNÓSTICA del área: no tiene `ambito` ni `id_programa`. Leer el
 * área desde una versión devuelve null en silencio — hay que partir de una
 * ejecución.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import Version from "@/interfaces/Versiones"
import type { ResultadoIndicador, ResultadoComponente, ResumenVersion } from '@/interfaces/ResultadoVersion'
import type { EstructuraVersion } from '@/interfaces/EstructuraVersion'
import { http } from '@/services/http'

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Version[]
 */
export async function obtenerVersiones(url: string): Promise<Version[]> {
  const result = await http.get<Version[]>(url)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Version | null
 */
export async function obtenerVersionPorId(url: string, id_version: number): Promise<Version | null> {
  const result = await http.get<Version>(`${url.replace(/\/$/, '')}/${id_version}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Version | null
 */
export async function obtenerVersionGraphPorId(url: string, id_version: number): Promise<Version | null> {
  const result = await http.get<Version>(`${url.replace(/\/$/, '')}/${id_version}/graph`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea versiones · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Version | null
 */
export async function crearVersionService(url: string, version: Omit<Version, 'id_version'>): Promise<Version | null> {
  const payload = { ...version, nombre: version.nombre?.trim() }
  const result = await http.post<Version>(url, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Actualiza versiones · petición `PUT`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Version | null
 */
export async function actualizarVersionService(url: string, id_version: number, version: Partial<Version>): Promise<Version | null> {
  const payload = { ...version, nombre: version.nombre?.trim() }
  const result = await http.put<Version>(`${url.replace(/\/$/, '')}/${id_version}`, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina versiones · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarVersionService(url: string, id_version: number): Promise<boolean> {
  const result = await http.delete(`${url.replace(/\/$/, '')}/${id_version}`)
  return result.ok
}

/**
 * Clona versiones.
 *
 * @returns Version | null
 */
export async function clonarVersionService(
  url: string,
  id_version: number,
  nuevoNombre?: string,
  ordinales?: string[],
  enfoque?: string
): Promise<Version | null> {
  const body = {
    ...(nuevoNombre ? { nuevo_nombre: nuevoNombre } : {}),
    // Clonación parcial: se envían los ordinales de los componentes a incluir.
    // El backend debe filtrar la estructura por estos ordinales (igual que en clonar modelo).
    ...(ordinales?.length ? { ordinales } : {}),
    // Enfoque elegido para la nueva versión (filtra aspectos por enfoque).
    ...(enfoque ? { enfoque } : {})
  }
  const result = await http.post<Version>(`${url.replace(/\/$/, '')}/${id_version}/clonar`, body)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Cambia versiones · petición `PATCH`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Version | null
 */
export async function cambiarEstadoVersionService(url: string, id_version: number, estado: string): Promise<Version | null> {
  const result = await http.patch<Version>(`${url.replace(/\/$/, '')}/${id_version}/estado`, { estado })
  return result.ok ? (result.data ?? null) : null
}

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns ResultadoIndicador[]
 */
export async function obtenerResultadosVersion(url: string, id_version: number): Promise<ResultadoIndicador[]> {
  const result = await http.get<ResultadoIndicador[]>(`${url.replace(/\/$/, '')}/${id_version}/resultados`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns ResultadoIndicador | null
 */
export async function obtenerResultadoIndicador(url: string, id_version: number, id_indicador: number): Promise<ResultadoIndicador | null> {
  const result = await http.get<ResultadoIndicador>(`${url.replace(/\/$/, '')}/${id_version}/resultados/${id_indicador}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns ResumenVersion | null
 */
export async function obtenerResumenVersion(url: string, id_version: number): Promise<ResumenVersion | null> {
  const result = await http.get<ResumenVersion>(`${url.replace(/\/$/, '')}/${id_version}/resultados/resumen`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Obtiene versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns ResultadoComponente | null
 */
export async function obtenerResultadoComponente(url: string, id_version: number, id_componente: number): Promise<ResultadoComponente | null> {
  const result = await http.get<ResultadoComponente>(`${url.replace(/\/$/, '')}/${id_version}/resultados/componente/${id_componente}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta versiones · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns EstructuraVersion | null
 */
export async function getEstructuraVersionService(url: string, id_version: number): Promise<EstructuraVersion | null> {
  const result = await http.get<EstructuraVersion>(`${url.replace(/\/$/, '')}/${id_version}/estructura`)
  return result.ok ? (result.data ?? null) : null
}
